import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ListPageSkeleton, type ListPageSkeletonProps } from './ListPageSkeleton';

export interface TitledListPageSkeletonProps extends ListPageSkeletonProps {
  /** 标题占位宽度类名（`CardTitle` 为 `leading-none` 16px 字，4 个汉字约 64px） */
  titleWidthClassName?: string;
  /** 标题行右侧操作按钮数量（如部门管理的「新增部门」、用户管理的「新增用户」） */
  headerActionCount?: number;
}

/**
 * 「页面标题 + 列表卡片」标准布局骨架。
 *
 * 对齐本仓库列表页的标准布局（`TaskCenter/CreateAdvertise`、`TeamManagement/DepartmentManagement`、
 * `TeamManagement/UserManagement`、投放管理的策略模板 / 草稿箱）：
 *
 * ```tsx
 * <div className="flex h-full flex-col gap-4">
 *   <div className="flex items-center justify-between"><CardTitle>标题</CardTitle>…</div>
 *   <div className="min-h-0 flex-1"><TableCardLayout parentRef={tableContainerRef} /></div>
 * </div>
 * ```
 *
 * `gap-4` 与 `min-h-0 flex-1` 都必须保留：前者是标题与卡片的间距，后者让卡片区能收缩，
 * 配合页面侧把 `parentRef` 指向这个 `flex-1` 容器，表格高度才会按「去掉标题行后的剩余高度」计算。
 */
export function TitledListPageSkeleton({
  titleWidthClassName = 'w-16',
  headerActionCount = 0,
  className,
  ...listProps
}: TitledListPageSkeletonProps) {
  return (
    <div className="flex h-full flex-col gap-4">
      {/* 页面标题行 */}
      <div className="flex items-center justify-between">
        <Skeleton className={cn('h-4', titleWidthClassName)} />
        {headerActionCount > 0 && (
          <div className="flex items-center gap-2">
            {Array.from({ length: headerActionCount }, (_, index) => (
              <Skeleton key={index} className="h-9 w-24" />
            ))}
          </div>
        )}
      </div>

      {/* 列表卡片 */}
      <div className="min-h-0 flex-1">
        <ListPageSkeleton className={className} {...listProps} />
      </div>
    </div>
  );
}
